import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useMutation } from "react-query";
import axios from "axios";

import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";

import { logout } from "../../Redux/actions/Auth";
import withAuth from "../../Hoc/withAuth";

function ActionRow({ icon: Icon, label, onClick, danger }) {
  const theme = useTheme();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      sx={{
        display:     "flex",
        alignItems:  "center",
        gap:         1.25,
        mx:          1,
        my:          0.25,
        px:          1.25,
        py:          0.875,
        borderRadius: 2,
        cursor:      "pointer",
        userSelect:  "none",
        color:       danger ? theme.palette.error.main : theme.palette.text.secondary,
        transition:  "all 0.15s ease",
        "&:hover": {
          bgcolor: danger
            ? alpha(theme.palette.error.main, 0.1)
            : alpha(theme.palette.text.primary, 0.05),
          color: danger ? theme.palette.error.main : theme.palette.text.primary,
        },
      }}
    >
      <Icon sx={{ fontSize: 18, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.875rem", fontWeight: 500 }}>{label}</Typography>
    </Box>
  );
}

/**
 * Bottom-of-drawer account actions for mobile: profile link + confirmed logout.
 * Mirrors the desktop TopBar logout flow (POST /logout → clear session → /login).
 */
function MobileDrawerActions({ token, user }) {
  const [open, setOpen] = useState(false);
  const theme    = useTheme();
  const router   = useHistory();
  const dispatch = useDispatch();
  const userUUID = user?.uuid;

  const logoutMutation = useMutation(() =>
    axios.post(
      `${process.env.REACT_APP_PUBLIC_URL}/auth/${userUUID}/logout`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    ),
  );

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (e) {
      // proceed with local logout regardless of network result
    }
    dispatch(logout());
    router.push("/login");
  };

  return (
    <Box sx={{ py: 0.5 }}>
      <ActionRow
        icon={PersonOutlineOutlinedIcon}
        label="My Profile"
        onClick={() => router.push("/profile")}
      />
      <ActionRow
        icon={LogoutOutlinedIcon}
        label="Log out"
        danger
        onClick={() => setOpen(true)}
      />

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { p: 1, border: `1px solid ${theme.palette.divider}` } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Confirm log out</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You're about to log out of your account. Any unsaved changes may be
            lost. You can log back in at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Stay logged in
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleLogout}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            Yes, log me out
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default withAuth(MobileDrawerActions);
